const SAVED_KEY = 'latebite-table-favourites-v1';
const uiText = (key, fallback) => document.body?.dataset[`text${key}`] || fallback;
const readSaved = storage => {
  try {
    const value = JSON.parse(storage?.getItem(SAVED_KEY) || '[]');
    return Array.isArray(value) ? [...new Set(value.filter(v => typeof v === 'string' && /^[a-z0-9-]{1,160}$/.test(v)))].slice(0, 100) : [];
  } catch { return []; }
};
const reduced = () => document.documentElement.dataset.motion === 'off' || window.matchMedia('(prefers-reduced-motion: reduce)').matches;
let toastTimer;
export function announce(root, message) {
  const toast = root.querySelector('[data-toast]');
  if (!toast) return;
  clearTimeout(toastTimer); toast.textContent = message; toast.classList.add('is-visible');
  toastTimer = setTimeout(() => toast.classList.remove('is-visible'), 4500);
}
export function initFavourites(root = document, storage, fetcher = globalThis.fetch) {
  try { storage ??= window.localStorage; } catch { /* In-memory selection remains available. */ }
  let saved = readSaved(storage);
  const section = root.querySelector('[data-saved-section]');
  const grid = root.querySelector('[data-saved-grid]');
  const status = root.querySelector('[data-saved-status]');
  const remote = new Map(), attempted = new Set(), inflight = new Map(), failed = new Set();
  let alive = true;
  const candidateRoot = document.body.dataset.cartRoot || '/';
  const storeRoot = /^\/(?!\/)[^?#\\]*$/.test(candidateRoot) ? candidateRoot.replace(/\/+$/, '') + '/' : '/';
  const originals = () => new Map([...root.querySelectorAll('.product-card[data-product-handle],article[data-product-handle]')].filter(card => !card.closest('[data-saved-grid]')).map(card => [card.dataset.productHandle, card]));
  function render() {
    if (grid) {
      const unique = originals(), existing = new Map([...grid.children].map(card => [card.dataset.productHandle, card]));
      const focused = grid.contains(document.activeElement) ? document.activeElement : null;
      const focusedCard = focused?.closest('[data-product-handle]');
      const focusIndex = focusedCard ? [...focusedCard.querySelectorAll('a,button,input,select')].indexOf(focused) : -1;
      const cards = saved.map(handle => {
        const card = unique.get(handle) || existing.get(handle) || remote.get(handle);
        if (!card) return null;
        const copy = unique.has(handle) || !existing.has(handle) ? card.cloneNode(true) : card;
        copy.hidden = false;
        copy.querySelectorAll('[id]').forEach(node => node.removeAttribute('id'));
        if (document.body.dataset.ordering !== 'true') copy.querySelectorAll('[data-add-form] button[type=submit],[data-add-form] button:not([type])').forEach(button => { button.disabled = true; });
        return copy;
      }).filter(Boolean);
      grid.replaceChildren(...cards);
      if (focusedCard && focusIndex >= 0) cards.find(card => card.dataset.productHandle === focusedCard.dataset.productHandle)?.querySelectorAll('a,button,input,select')[focusIndex]?.focus({ preventScroll: true });
      if (section) section.hidden = saved.length === 0;
      const loading = saved.some(handle => !unique.has(handle) && !remote.has(handle) && (!attempted.has(handle) || inflight.has(handle)));
      grid.setAttribute('aria-busy', String(loading));
      if (status) status.textContent = !saved.length ? '' : loading ? section?.dataset.loading || 'Loading your saved picks…' : failed.size ? section?.dataset.error || 'Some saved picks could not be loaded. Refresh to try again.' : !cards.length ? section?.dataset.unavailable || 'Your saved picks are not currently available in the shop.' : '';
    }
    root.querySelectorAll('[data-favourite]').forEach(button => {
      const handle = button.closest('[data-product-handle]')?.dataset.productHandle;
      button.setAttribute('aria-pressed', String(saved.includes(handle)));
    });
  }
  function hydrate() {
    if (!alive || !grid || !fetcher) return;
    const present = originals();
    const queue = saved.filter(handle => !present.has(handle) && !remote.has(handle) && !attempted.has(handle));
    for (const handle of queue.slice(0, Math.max(0, 3 - inflight.size))) {
      const controller = new AbortController(); attempted.add(handle); inflight.set(handle, controller);
      const timeout = setTimeout(() => controller.abort(), 8000);
      void (async () => {
        try {
          const response = await fetcher(`${storeRoot}products/${encodeURIComponent(handle)}?section_id=midnight-saved-card`, { credentials: 'same-origin', mode: 'same-origin', cache: 'no-store', signal: controller.signal });
          if (!response.ok) { if (response.status !== 404) failed.add(handle); return; }
          if (response.url && new URL(response.url, window.location.href).origin !== window.location.origin) return;
          const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
          const card = parsed.querySelector('[data-saved-product] > .product-card[data-product-handle]');
          if (!alive || !saved.includes(handle) || card?.dataset.productHandle !== handle) return;
          card.querySelectorAll('script,iframe,object,embed').forEach(node => node.remove());
          remote.set(handle, document.importNode(card, true));
        } catch { if (alive) failed.add(handle); }
        finally {
          clearTimeout(timeout); inflight.delete(handle);
          if (alive) { hydrate(); render(); }
        }
      })();
    }
  }
  function onClick(event) {
    const button = event.target.closest?.('[data-favourite]');
    if (!button || !root.contains(button)) return;
    const handle = button.closest('[data-product-handle]')?.dataset.productHandle;
    if (!handle) return;
    const wasSaved = saved.includes(handle);
    if (!wasSaved && saved.length >= 100) { announce(root, uiText('SavedFull', 'Your saved list is full. Remove a pick to save another.')); return; }
    const inSavedGrid = Boolean(button.closest('[data-saved-grid]'));
    saved = wasSaved ? saved.filter(value => value !== handle) : [...saved, handle];
    if (wasSaved) failed.delete(handle);
    try { storage?.setItem(SAVED_KEY, JSON.stringify(saved)); } catch { /* No persistence in private storage modes. */ }
    hydrate(); render();
    // Removing a saved clone must not throw keyboard focus back to the document.
    if (inSavedGrid && wasSaved) {
      const target = originals().get(handle)?.querySelector('[data-favourite]') || grid?.querySelector('[data-favourite]') || root.querySelector('.category-nav a');
      target?.focus({ preventScroll: true });
    }
    announce(root, wasSaved ? uiText('Removed', 'Removed from saved picks') : uiText('Saved', 'Saved on this device'));
  }
  function onStorage(event) { if (event.key === SAVED_KEY || event.key === null) { saved = readSaved(storage); for (const handle of failed) if (!saved.includes(handle)) failed.delete(handle); hydrate(); render(); } }
  root.addEventListener('click', onClick); window.addEventListener('storage', onStorage); hydrate(); render();
  return () => { alive = false; inflight.forEach(controller => controller.abort()); root.removeEventListener('click', onClick); window.removeEventListener('storage', onStorage); };
}
function flyToBag(form, bag) {
  const source = form.closest('[data-product-handle]')?.querySelector('.product-media>a>img,.product-media>img:not(.photo-stamp)');
  if (!source || !bag || reduced()) return;
  const start = source.getBoundingClientRect(), end = bag.getBoundingClientRect();
  const flight = source.cloneNode();
  ['id', 'srcset', 'sizes', 'loading', 'fetchpriority', 'class'].forEach(name => flight.removeAttribute(name));
  flight.alt = ''; flight.setAttribute('aria-hidden', 'true'); flight.dataset.cartFlight = '';
  Object.assign(flight.style, { position: 'fixed', zIndex: '90', pointerEvents: 'none', objectFit: 'contain', left: `${start.left}px`, top: `${start.top}px`, width: `${start.width}px`, height: `${start.height}px`, '--flight-x': `${end.left + end.width / 2 - start.left - start.width / 2}px`, '--flight-y': `${end.top + end.height / 2 - start.top - start.height / 2}px` });
  document.body.append(flight);
  const timeout = setTimeout(() => flight.remove(), 900);
  flight.addEventListener('animationend', () => { clearTimeout(timeout); flight.remove(); }, { once: true });
  bag.classList.remove('is-catching');
  requestAnimationFrame(() => { if (!reduced()) bag.classList.add('is-catching'); });
}
export function initCart(root = document, fetcher = fetch) {
  const pending = new Set(); let cartRevision = 0;
  const uncertainMessage = uiText('AddUncertain', "We couldn't confirm this addition. Check your bag before adding again.");
  const submitButton = form => form.querySelector('button[type=submit],button:not([type])');
  function markPending(id, source) {
    for (const form of new Set([source, ...root.querySelectorAll('[data-add-form]')])) {
      if (form.dataset.cartPending || new FormData(form).get('id') !== id) continue;
      form.dataset.cartPending = id; form.setAttribute('aria-busy', 'true');
      const button = submitButton(form);
      if (button) { button.dataset.cartWasDisabled = String(button.disabled); button.disabled = true; }
    }
  }
  function clearPending(id, source) {
    // Saved cards can be cloned or replaced while this request is in flight.
    for (const form of new Set([source, ...root.querySelectorAll('[data-add-form]')])) {
      if (form.dataset.cartPending !== id) continue;
      delete form.dataset.cartPending; form.removeAttribute('aria-busy');
      const button = submitButton(form), select = form.querySelector('[data-variant-select]');
      if (button) {
        button.disabled = document.body.dataset.ordering !== 'true' || (select ? select.selectedOptions[0]?.dataset.available !== 'true' : button.dataset.cartWasDisabled === 'true');
        delete button.dataset.cartWasDisabled;
      }
    }
  }
  async function refreshCart(cartRoot, revision) {
    try {
      const response = await fetcher(`${cartRoot}cart.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin', cache: 'no-store' });
      if (!response.ok) return;
      const cart = await response.json();
      if (revision === cartRevision && Number.isSafeInteger(cart.item_count) && cart.item_count >= 0) {
        root.querySelectorAll('[data-cart-count]').forEach(count => { count.textContent = String(cart.item_count); });
        document.dispatchEvent(new CustomEvent('latebite:cart-refresh', { detail: { cart, source: 'product-add' } }));
      }
    } catch { /* A failed cart read must never cause another add request. */ }
  }
  async function onSubmit(event) {
    const form = event.target.closest?.('[data-add-form]');
    if (!form || !root.contains(form)) return;
    event.preventDefault();
    if (document.body.dataset.ordering !== 'true') { announce(root, uiText('Preview', 'Ordering is not available in this preview.')); return; }
    const button = submitButton(form);
    if (form.dataset.cartPending || button?.disabled || !form.reportValidity()) return;
    const data = new FormData(form), id = data.get('id'), quantity = Number(data.get('quantity') || 1);
    if (pending.has(id)) return;
    if (!Number.isSafeInteger(quantity) || quantity < 1 || quantity > 99) { announce(root, uiText('QuantityError', 'Choose a quantity between 1 and 99.')); return; }
    pending.add(id); markPending(id, form);
    const cartRoot = (document.body.dataset.cartRoot || '/').replace(/\/+$/, '') + '/';
    let accepted = false;
    document.dispatchEvent(new CustomEvent('latebite:cart-dirty'));
    try {
      const response = await fetcher(`${cartRoot}cart/add.js`, { method: 'POST', headers: { Accept: 'application/json' }, credentials: 'same-origin', body: data });
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        // Shopify can add the available stock and still return a 422 error.
        await refreshCart(cartRoot, ++cartRevision);
        announce(root, response.status >= 500 ? uncertainMessage : error?.description || uiText('AddError', 'Could not add this item. Please check your bag.'));
        return;
      }
      accepted = true;
      // An accepted HTTP add is success even if its response body is unexpectedly unreadable.
      await response.json().catch(() => null);
      const revision = ++cartRevision;
      const counts = [...root.querySelectorAll('[data-cart-count]')];
      const current = Number(counts[0]?.textContent || 0);
      counts.forEach(count => { count.textContent = String((Number.isSafeInteger(current) ? current : 0) + quantity); });
      flyToBag(form, root.querySelector('[data-bag-target]')); announce(root, uiText('Added', 'Added to your bag'));
      await refreshCart(cartRoot, revision);
    } catch {
      await refreshCart(cartRoot, ++cartRevision);
      announce(root, accepted ? uiText('Added', 'Added to your bag') : uncertainMessage);
    } finally {
      pending.delete(id); clearPending(id, form);
    }
  }
  root.addEventListener('submit', onSubmit);
  return () => root.removeEventListener('submit', onSubmit);
}
export function initVariants(root = document) {
  function update(select) {
    const option = select.selectedOptions[0], card = select.closest('[data-product-handle]');
    if (!option || !card) return;
    const price = card.querySelector('[data-product-price]'), unit = card.querySelector('[data-unit-price]');
    if (price) price.textContent = option.dataset.price || '';
    if (unit) unit.textContent = option.dataset.unit || '';
    const button = card.querySelector('[data-variant-submit]'), label = card.querySelector('[data-variant-label]');
    const enabled = document.body.dataset.ordering === 'true', available = option.dataset.available === 'true';
    if (button) button.disabled = Boolean(select.closest('form')?.dataset.cartPending) || !enabled || !available;
    if (label) label.textContent = !enabled ? uiText('Preview', 'Preview only') : available ? uiText('Add', 'Add to bag') : uiText('SoldOut', 'Sold out');
  }
  const onChange = event => { if (event.target.matches('[data-variant-select]')) update(event.target); };
  root.querySelectorAll('[data-variant-select]').forEach(update); root.addEventListener('change', onChange);
  return () => root.removeEventListener('change', onChange);
}
function boot() {
  if (!document.querySelector('[data-midnight-root]')) return;
  window.__midnightCommerceCleanup?.();
  const cleanups = [initFavourites(), initCart(), initVariants()];
  window.__midnightCommerceCleanup = () => cleanups.forEach(fn => fn());
}
if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true }); else boot();
  document.addEventListener('shopify:section:load', boot);
}

